/**
 * Catalog import: persist original file row as labeled `key=value` pairs
 * (`;`-joined, with `\`, `;`, `=` escaped in values).
 */

export type ImportSourceField = { label: string; value: string };

/** Canonical product template column order (fallback labels for legacy unlabeled lines). */
export const IMPORT_SOURCE_FALLBACK_HEADERS = [
  'slug_produit',
  'nom',
  'description',
  'marque_slug',
  'marque_nom',
  'gamme_slug',
  'gamme_nom',
  'categorie_slug',
  'categorie_nom',
  'sous_categorie_slug',
  'sous_categorie_nom',
  'type_produit_code',
  'type_produit_nom',
  'prix_public_ttc',
  'prix_grossiste_ttc',
  'miniature_cle',
  'miniature_url',
  'images_cles',
  'images_urls',
  'tags',
] as const;

export function escapeImportSourceValue(v: string): string {
  return String(v).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/=/g, '\\=');
}

export function unescapeImportSourceValue(v: string): string {
  let out = '';
  for (let i = 0; i < v.length; i++) {
    if (v[i] === '\\' && i + 1 < v.length) {
      out += v[i + 1]!;
      i++;
      continue;
    }
    out += v[i]!;
  }
  return out;
}

/** Serialize header-aligned record as labeled source line. */
export function serializeImportSourceLine(
  headers: string[],
  record: Record<string, string>,
): string {
  return headers
    .map((k) => (typeof k === 'string' ? k.trim() : ''))
    .filter(Boolean)
    .map((k) => `${k}=${escapeImportSourceValue(record[k] ?? '')}`)
    .join(';');
}

function splitEscapedDelimiter(line: string, delim: string): string[] {
  const out: string[] = [];
  let cur = '';
  for (let i = 0; i < line.length; i++) {
    if (line[i] === '\\' && i + 1 < line.length) {
      cur += line[i]! + line[i + 1]!;
      i++;
      continue;
    }
    if (line[i] === delim) {
      out.push(cur);
      cur = '';
      continue;
    }
    cur += line[i]!;
  }
  out.push(cur);
  return out;
}

function indexOfUnescaped(haystack: string, needle: string): number {
  for (let i = 0; i < haystack.length; i++) {
    if (haystack[i] === '\\' && i + 1 < haystack.length) {
      i++;
      continue;
    }
    if (haystack[i] === needle) return i;
  }
  return -1;
}

function looksLabeled(segments: string[]): boolean {
  if (!segments.length) return false;
  let labeled = 0;
  for (const seg of segments) {
    const eq = indexOfUnescaped(seg, '=');
    if (eq <= 0) continue;
    const key = unescapeImportSourceValue(seg.slice(0, eq)).trim();
    if (/^[a-zA-Z_][\w.-]*$/.test(key)) labeled++;
  }
  return labeled >= Math.max(1, Math.ceil(segments.length * 0.5));
}

/**
 * Parse stored import source line into labeled fields.
 * Supports labeled `key=value;…` and legacy unlabeled `;` / `,` cells.
 */
export function parseImportSourceFields(
  line: string,
  headers?: string[] | null,
): ImportSourceField[] {
  const trimmed = line.trim();
  if (!trimmed) return [];

  const delim = trimmed.includes(';') ? ';' : trimmed.includes(',') ? ',' : null;
  if (!delim) {
    return [{ label: 'valeur', value: trimmed }];
  }

  const segments = splitEscapedDelimiter(trimmed, delim).map((s) => s.trim());
  if (!segments.length) return [];

  if (looksLabeled(segments)) {
    return segments
      .map((seg) => {
        const eq = indexOfUnescaped(seg, '=');
        if (eq <= 0) {
          return { label: 'valeur', value: unescapeImportSourceValue(seg) };
        }
        return {
          label: unescapeImportSourceValue(seg.slice(0, eq)).trim() || 'valeur',
          value: unescapeImportSourceValue(seg.slice(eq + 1)),
        };
      })
      .filter((f) => f.label || f.value);
  }

  const labels =
    headers && headers.length === segments.length
      ? headers
      : segments.length === IMPORT_SOURCE_FALLBACK_HEADERS.length
        ? [...IMPORT_SOURCE_FALLBACK_HEADERS]
        : null;

  return segments.map((value, i) => ({
    label: (labels?.[i] ?? `col_${i + 1}`).trim() || `col_${i + 1}`,
    value,
  }));
}
